<template></template>

<script setup>
import { useLocalStorage } from "@vueuse/core";
import { useCookies } from "@vueuse/integrations/useCookies";
import { differenceInSeconds } from "date-fns";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import LoadingSpinner from "@/components/LoadingSpinner.vue";
import useNotificationStore from "@/stores/notifications";
import useUserStore from "@/stores/user";

const notificationStore = useNotificationStore();
const router = useRouter();
const errorDisplay = ref();
const userStore = useUserStore();
const lastLoginAttempt = useLocalStorage("lastLoginAttempt", null);
const authReturnPath = useLocalStorage("authReturnPath", "/");

async function handleCognitoHash() {
  const pendingLoginNotificationId = notificationStore.addNotification(
    "Logging you in, one momment...",
    "blue",
  );

  if (lastLoginAttempt.value) {
    const diff = differenceInSeconds(new Date(), lastLoginAttempt.value);
    if (diff < 30) {
      notificationStore.removeNotification(pendingLoginNotificationId);
      notificationStore.addNotification(
        `You've attempted logging in too recently. Please try again in a ${30 - diff} seconds.`,
        "red",
      );
      errorDisplay.value = lastLoginAttempt.value = null;
      return;
    }
  }

  lastLoginAttempt.value = new Date();

  try {
    const hash = window.location.hash.replace("#", "");
    const cookies = useCookies(["csrftoken"]);
    const urlParams = new URLSearchParams(hash);
    const response = await fetch("/authenticate/", {
      method: "POST",
      body: JSON.stringify({ id_token: urlParams.get("id_token") }),
      headers: { "X-CSRFToken": cookies.get("csrftoken") },
    });

    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }

    await router.push(authReturnPath.value);
    notificationStore.removeNotification(pendingLoginNotificationId);
    notificationStore.addNotification("Log in successful!", "green", 2000);
  } catch (error) {
    notificationStore.removeNotification(pendingLoginNotificationId);
    notificationStore.addNotification(
      `Login failed. Please try again in a few minutes`,
      "red",
    );
    throw error;
  }
}

async function getCognitoLoginHash() {
  try {
    const response = await fetch("/login_url/");
    if (!response.ok) {
      throw new Error("Log in failed");
    }
    const data = await response.json();
    const loginUrl = await data.login_url;
    if (!loginUrl) {
      throw new Error("Login in failed: did not get a valid login url");
    }

    const redirectUri = `${window.location.protocol}//${window.location.host}/login/`;
    window.location.href = `${loginUrl}&redirect_uri=${redirectUri}`;
  } catch (error) {
    notificationStore.addNotification(
      `Log in failed. Please try again in a few minutes`,
      "red",
    );

    throw error;
  }
}

onMounted(async () => {
  if (window.location.hash) {
    // if there's a hash, that means we are back from cognitoland
    await handleCognitoHash();
  } else {
    // theres no hash! head out to cognitoland
    await getCognitoLoginHash();
  }
});
</script>
